import React, { useContext, useEffect, useState } from 'react';

import { useHistory } from "react-router-dom";
import { Row, Col } from 'react-bootstrap';

import ErrorPage from '../Containers/ErrorPage';
import AppContext from '../App.context';
import DataSource from '../../config/api';
import DefaultLoader from './DefaultLoader';

import AppointmentForm from './AppointmentForm';
import AppointmentDetails from './AppointmentDetails';
import Footer from '../Containers/Footer';

import '../../style/landingPage.css';
import ConstUtils from '../../config/constants';

export default function LandingPage(props) {
    const history = useHistory();

    const [loading, setLoading] = useState(true);
    const [utmParams, setUtmParams] = useState({});
    const [caseDetails, setCaseDetails] = useState({});
    const [userPincode, setUserPincode] = useState('');

    const [isError, setIsError] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [isBookingAllowed, setIsBookingAllowed] = useState(true);

    const { setApplicationState, setEntityDetails } = useContext(AppContext);

    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    const utm_params = {
        utm_mode: params["utm_mode"],
        utm_actor: params["utm_actor"],
        utm_fcount: params["utm_fcount"],
        utm_trigger: params["utm_trigger"],
    };

    useEffect(() => {
        async function loadDetails() {
            await decryptBookingDetails();
        }
        loadDetails();
    }, []);

    const checkLocationPermissionState = async () => {
        try {
            const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
            return permissionStatus.state;
        } catch (error) {
            return "undetermined";
        }
    };

    const promptAndGetLocation = async () => {
        const positionOptions = {
            enableHighAccuracy: false,
            timeout: 2000,
            maximumAge: 0
        };

        return new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition((position) => {
            resolve(position.coords);
        }, (error) => {
            reject(error);
        }, positionOptions));
    };

    const fetchUserLocation = async () => {
        let locationCoords = null;
        const permissionState = await checkLocationPermissionState();

        if (["prompt", "undetermined", "granted", "denied"].includes(permissionState)) {
            try {
                locationCoords = await promptAndGetLocation();
            } catch (error) {
                throw error;
            }
        } else {
            throw new Error("Geolocation permission was in a denied state");
        }

        return locationCoords;
    };

    const fetchLocationDetails = async () => {
        try {
            const userLocation = await fetchUserLocation();
            const { latitude, longitude } = userLocation;

            DataSource.postActivity({
                campaignMode: "",
                RequestId: params['id'],
                EQuote: "",
                EventType: "Location_Access_Allowed",
                EventPayload: "",
                meta: JSON.stringify({utm_params, userLocation}),
                campaignId: "202010201700"
            });

            const userPincode = await DataSource.fetchPincodeFromCoordinates(latitude, longitude);

            setUserPincode(userPincode);
        } catch (err) {
            console.log("Location Access Denied", err);
            DataSource.postActivity({
                campaignMode: "",
                RequestId: params['id'],
                EQuote: "",
                EventType: "Location_Access_Denied",
                EventPayload: "",
                meta: JSON.stringify(utm_params),
                campaignId: "202010201700"
            });
        }
    };


    const decryptBookingDetails = async () => {
        const beforeLoadTime = new Date();

        DataSource.postActivity({
            campaignMode: "",
            RequestId: params['id'],
            EQuote: "",
            EventType: "Clicked_On_Sal",
            EventPayload: "",
            meta: JSON.stringify(utm_params),
            campaignId: "202010201700"
        });

        try {
            const response = await DataSource.fetchData(Object.keys(params)[0], params['id']);
            const caseData = response.caseDetails;

            if (response.success === true) {
                setUtmParams(utm_params);
                setCaseDetails(caseData);
                setApplicationState(caseData ? caseData.campaignType : null);
                setEntityDetails({
                    insurerId: caseData?.EntityRelationId,
                    insurerName: caseData?.insuranceCompanyName
                });

                if (!ConstUtils.AllowedStatusesToBookAppointment.includes(caseData.tblStatus || 0)) {
                    setIsBookingAllowed(false);
                    
                    history.push("/appointment", {
                        address: `${caseData.appointmentAddress}`,
                        requestId: caseData.id,
                        caseDetails: caseData,
                        slotDetails: {
                            appointmentDate: caseData.appointmentDate, 
                            appointmentTime: caseData.appointmentTime
                        },
                        showAddress: caseData.appointmentAddress ? true : false,
                        landedFromAppointmentPage: false,
                    });
                }
            } else {
                setIsError(true);
                setIsBookingAllowed(false);
                setAlertMessage("Unable to open the booking form at the moment please try again.");
            }

            DataSource.postActivity({
                campaignMode: caseData.campaignType,
                RequestId: caseData.id,
                EQuote: "",
                EventType: isBookingAllowed ? "Landed_on_unbooked_page_v2" : "AppointmentAlreadyBooked",
                EventPayload: { "alreadyBooked": !response.success, loadTime: new Date() - beforeLoadTime },
                meta: JSON.stringify(utm_params),
                campaignId: caseData.campaignId
            });

            if(isBookingAllowed) {
                await fetchLocationDetails();
            }

        } catch (err) {
            console.log(err);
            setIsError(true);
            setIsBookingAllowed(false);

            DataSource.postActivity({
                campaignMode: "",
                RequestId: params['id'],
                EQuote: "",
                EventType: err?.response?.status === 409 ? "AppointmentAlreadyBooked" : "ERROR_WHILE_LANDING",
                EventPayload: "",
                campaignId: "202010201700",
                meta: JSON.stringify({
                    utmParams: utm_params,
                    error: err?.response?.data?.error ?? err?.message
                }),
            });

            if (err?.isAxiosError) {
                if (err?.response?.status === 409) {
                    setIsError(false);
                    const caseData = err?.response?.data?.caseDetails;
                    if(caseData) {
                        history.push("/appointment", {
                            address: `${caseData.appointmentAddress}`,                            
                            requestId: caseData?.id,
                            caseDetails: caseData,
                            slotDetails: {
                                appointmentDate: caseData?.appointmentDate,
                                appointmentTime: caseData?.appointmentTime
                            },
                            showAddress: caseData.appointmentAddress ? true : false,
                            landedFromAppointmentPage: false,
                        });
                        return;
                    }
                }
                setAlertMessage('Error occured while opening the booking form please try again');
            }
        }
        setLoading(false);
    };

    return (
        <>
            {
                loading ?
                    <DefaultLoader /> :
                    isBookingAllowed ?
                        <div>
                            <div className='landing-page'>
                                <Row>
                                    <Col xs={12} sm={12} md={12} lg={4}>
                                        <AppointmentDetails
                                            utmParams={utmParams}
                                            caseDetails={caseDetails}
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={12} lg={8}>
                                        <AppointmentForm
                                            utmParams={utmParams}
                                            caseDetails={caseDetails}
                                            historyData={props.historyData}
                                            userPincode={userPincode}
                                        />
                                    </Col>
                                </Row>
                            </div>
                            <Footer
                                isFooterFixed={false}
                            />
                        </div> :
                        <ErrorPage
                            errorMessage={alertMessage}
                            imgSrc={isError ? `${ConstUtils.StaticS3ImageUrl}/Error.svg` : `${ConstUtils.StaticS3ImageUrl}/Success.svg`}
                        />
            }
        </>
    );
};